import React, { useMemo, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useStore } from "../context/StoreContext";
import api, { money, formatApiError } from "../lib/api";

export default function CheckoutPage() {
  const { cart, cartSubtotal, clearCart, user, bootstrap, buyNow, setBuyNow } = useStore();
  const items = buyNow ? [buyNow] : cart;
  const subtotal = buyNow ? buyNow.price * buyNow.quantity : cartSubtotal;
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: user?.name || "", email: user?.email || "", phone: "", cpf: "",
    cep: "", street: "", number: "", complement: "", city: "", state: "",
  });
  const [coupon, setCoupon] = useState("");
  const [couponResult, setCouponResult] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [loading, setLoading] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const lastCep = useRef("");

  const onCepChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 8);
    const masked = digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
    setForm((f) => ({ ...f, cep: masked }));
    if (digits.length === 8 && lastCep.current !== digits) {
      lastCep.current = digits;
      setCepLoading(true);
      fetch(`https://viacep.com.br/ws/${digits}/json/`)
        .then((r) => r.json())
        .then((d) => {
          if (d.erro) {
            toast.error("CEP não encontrado — confira e preencha manualmente");
            return;
          }
          setForm((f) => ({
            ...f,
            street: d.logradouro || f.street,
            city: d.localidade || f.city,
            state: d.uf || f.state,
          }));
          toast.success("Endereço preenchido pelo CEP — confira o número");
        })
        .catch(() => toast.error("Não foi possível buscar o CEP — preencha manualmente"))
        .finally(() => setCepLoading(false));
    }
  };

  const hasPhysical = items.some((i) => i.type === "physical");
  const shippingCfg = bootstrap?.settings || {};
  const shipping = useMemo(() => {
    if (!hasPhysical) return 0;
    const freeAbove = shippingCfg.shipping_free_above || 0;
    const effective = subtotal - (couponResult?.discount || 0);
    if (freeAbove && effective >= freeAbove) return 0;
    return shippingCfg.shipping_flat || 0;
  }, [hasPhysical, subtotal, couponResult, shippingCfg]);

  const discount = couponResult?.discount || 0;
  const total = subtotal - discount + shipping;
  const promoSavings = items.reduce((s, i) => s + ((i.base_price || i.price) - i.price) * i.quantity, 0);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const applyCoupon = async () => {
    setCouponError("");
    setCouponResult(null);
    try {
      const r = await api.post("/coupons/validate", { code: coupon, subtotal, promo_savings: promoSavings });
      if (r.data.promo_bigger) {
        setCouponError("A promoção deste item já é maior que o cupom — vale o maior desconto.");
        return;
      }
      setCouponResult(r.data);
      toast.success(`Cupom ${r.data.code} aplicado`);
    } catch (e) {
      setCouponError(formatApiError(e, "Cupom inválido"));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!items.length) return;
    setLoading(true);
    try {
      const payload = {
        customer: { name: form.name, email: form.email, phone: form.phone, cpf: form.cpf },
        address: hasPhysical ? {
          cep: form.cep.replace(/\D/g, ""), street: form.street, number: form.number,
          complement: form.complement, city: form.city, state: form.state,
        } : null,
        items: items.map((i) => ({ product_id: i.product_id, variation_id: i.variation_id, quantity: i.quantity })),
        coupon_code: couponResult?.code || null,
      };
      const r = await api.post("/checkout", payload);
      if (buyNow) setBuyNow(null);
      else clearCart();
      window.location.href = r.data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err, "Não foi possível criar o pedido"));
      setLoading(false);
    }
  };

  if (!items.length) {
    return (
      <div><Header />
        <main className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="checkout-empty">
          <p className="text-zinc-400 mb-6">Seu carrinho está vazio.</p>
          <Button asChild className="bg-gold text-black hover:bg-gold-light"><Link to="/">Voltar à loja</Link></Button>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div data-testid="checkout-page">
      <Header />
      <main className="max-w-6xl mx-auto px-6 lg:px-12 py-14">
        <h1 className="font-display text-3xl sm:text-4xl tracking-tighter font-medium mb-12" data-testid="checkout-title">Finalizar compra</h1>
        {buyNow && (
          <div className="mb-8 flex items-center justify-between gap-4 border border-gold/30 rounded-lg px-5 py-3 bg-amber-500/5" data-testid="buynow-banner">
            <p className="text-sm text-zinc-300 min-w-0">Compra direta: <span className="text-gold">{buyNow.name}</span> — somente este item.</p>
            <button type="button" onClick={() => setBuyNow(null)} data-testid="buynow-cancel" className="text-xs text-zinc-500 hover:text-gold transition-colors shrink-0">Voltar ao carrinho completo</button>
          </div>
        )}
        <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          <div className="lg:col-span-3 space-y-10">
            <section>
              <p className="label-caps mb-5">Seus dados</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="sm:col-span-2">
                  <Label htmlFor="co-name">Nome completo *</Label>
                  <Input id="co-name" data-testid="checkout-name" required value={form.name} onChange={set("name")}
                    className="mt-1.5 bg-[#141414] border-white/10 focus:border-amber-500" />
                </div>
                <div>
                  <Label htmlFor="co-email">E-mail *</Label>
                  <Input id="co-email" data-testid="checkout-email" type="email" required value={form.email} onChange={set("email")}
                    className="mt-1.5 bg-[#141414] border-white/10 focus:border-amber-500" />
                </div>
                <div>
                  <Label htmlFor="co-phone">Telefone / WhatsApp</Label>
                  <Input id="co-phone" data-testid="checkout-phone" value={form.phone} onChange={set("phone")}
                    className="mt-1.5 bg-[#141414] border-white/10 focus:border-amber-500" />
                </div>
                <div>
                  <Label htmlFor="co-cpf">CPF (para o certificado de autenticidade)</Label>
                  <Input id="co-cpf" data-testid="checkout-cpf" value={form.cpf} onChange={set("cpf")} placeholder="000.000.000-00"
                    className="mt-1.5 bg-[#141414] border-white/10 focus:border-amber-500" />
                  <p className="text-[11px] text-zinc-600 mt-1.5">Só os 3 últimos dígitos aparecem na verificação pública.</p>
                </div>
              </div>
              {!user && (
                <p className="text-xs text-zinc-500 mt-4">
                  Comprando como convidado. <Link to="/login" className="text-gold hover:underline" data-testid="checkout-login-link">Entre ou crie uma conta</Link> para acompanhar seus pedidos.
                </p>
              )}
            </section>

            {hasPhysical && (
              <section>
                <p className="label-caps mb-5">Endereço de entrega</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="co-cep">CEP *</Label>
                    <Input id="co-cep" data-testid="checkout-cep" required value={form.cep} onChange={onCepChange} placeholder="00000-000" inputMode="numeric" className="mt-1.5 bg-[#141414] border-white/10 focus:border-amber-500" />
                    <p className="text-[11px] text-zinc-600 mt-1.5" data-testid="cep-hint">{cepLoading ? "Buscando endereço..." : "Rua, cidade e estado são preenchidos automaticamente."}</p>
                  </div>
                  <div>
                    <Label htmlFor="co-street">Rua *</Label>
                    <Input id="co-street" data-testid="checkout-street" required value={form.street} onChange={set("street")} className="mt-1.5 bg-[#141414] border-white/10 focus:border-amber-500" />
                  </div>
                  <div>
                    <Label htmlFor="co-number">Número *</Label>
                    <Input id="co-number" data-testid="checkout-number" required value={form.number} onChange={set("number")} className="mt-1.5 bg-[#141414] border-white/10 focus:border-amber-500" />
                  </div>
                  <div>
                    <Label htmlFor="co-complement">Complemento</Label>
                    <Input id="co-complement" data-testid="checkout-complement" value={form.complement} onChange={set("complement")} className="mt-1.5 bg-[#141414] border-white/10 focus:border-amber-500" />
                  </div>
                  <div>
                    <Label htmlFor="co-city">Cidade *</Label>
                    <Input id="co-city" data-testid="checkout-city" required value={form.city} onChange={set("city")} className="mt-1.5 bg-[#141414] border-white/10 focus:border-amber-500" />
                  </div>
                  <div>
                    <Label htmlFor="co-state">Estado *</Label>
                    <Input id="co-state" data-testid="checkout-state" required value={form.state} onChange={set("state")} className="mt-1.5 bg-[#141414] border-white/10 focus:border-amber-500" />
                  </div>
                </div>
              </section>
            )}
          </div>

          <aside className="lg:col-span-2">
            <div className="border border-white/10 rounded-lg p-7 bg-[#111111] gold-glow sticky top-24" data-testid="order-summary">
              <p className="label-caps mb-6">Resumo do pedido</p>
              <div className="space-y-4 max-h-64 overflow-y-auto pr-1">
                {items.map((i) => (
                  <div key={`${i.product_id}:${i.variation_id}`} className="flex justify-between gap-3 text-sm">
                    <span className="text-zinc-300 min-w-0"><span className="text-zinc-500">{i.quantity}×</span> {i.name} <span className="text-zinc-500">({i.variation_name})</span></span>
                    <span className="text-zinc-200 shrink-0">{money(i.price * i.quantity)}</span>
                  </div>
                ))}
              </div>

              <div className="mt-6 pt-5 border-t border-white/10">
                <Label htmlFor="co-coupon">Cupom de desconto</Label>
                <div className="flex gap-2 mt-1.5">
                  <Input id="co-coupon" data-testid="coupon-input" value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                    placeholder="BEMVINDO10" className="bg-[#141414] border-white/10 focus:border-amber-500" />
                  <Button type="button" variant="outline" data-testid="apply-coupon-button" onClick={applyCoupon}
                    className="border-gold/40 text-gold hover:bg-amber-500/10 shrink-0">Aplicar</Button>
                </div>
                {couponError && <p className="text-xs text-red-400 mt-2" data-testid="coupon-error">{couponError}</p>}
                {couponResult && <p className="text-xs text-gold mt-2" data-testid="coupon-success">Cupom {couponResult.code}: -{money(couponResult.discount)}</p>}
                <p className="text-[11px] text-zinc-600 mt-2">Cupom e promoção não acumulam — vale o maior desconto.</p>
              </div>

              <div className="mt-6 pt-5 border-t border-white/10 space-y-2.5 text-sm">
                <div className="flex justify-between text-zinc-400"><span>Subtotal</span><span data-testid="summary-subtotal">{money(subtotal)}</span></div>
                {discount > 0 && <div className="flex justify-between text-gold"><span>Desconto</span><span data-testid="summary-discount">-{money(discount)}</span></div>}
                <div className="flex justify-between text-zinc-400">
                  <span>Frete</span>
                  <span data-testid="summary-shipping">{hasPhysical ? (shipping === 0 ? "Grátis" : money(shipping)) : "—"}</span>
                </div>
                <div className="flex justify-between pt-3 border-t border-white/10">
                  <span className="text-zinc-200">Total</span>
                  <span className="font-display text-2xl text-gold" data-testid="summary-total">{money(total)}</span>
                </div>
              </div>

              <Button type="submit" disabled={loading} data-testid="place-order-button"
                className="w-full mt-7 bg-gold text-black hover:bg-gold-light font-semibold h-12 transition-colors disabled:opacity-50">
                {loading ? "Criando pedido..." : "Ir para o pagamento"}
              </Button>
              <p className="text-[11px] text-zinc-600 mt-3 text-center">Você será redirecionado para o pagamento seguro (InfinitePay).</p>
            </div>
          </aside>
        </form>
      </main>
      <Footer />
    </div>
  );
}
